import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
  onAuthStateChanged, isSignInWithEmailLink, signInWithEmailLink, signOut,
} from 'firebase/auth';
import { api } from './api';
import { initFirebase, getFirebaseAuth, requestNotificationPermission, setupForegroundNotifications } from './firebase-init';
import Setup from './components/Setup';
import StudentView from './components/StudentView';
import AdminView from './components/AdminView';
import VideoAdmin from './components/VideoAdmin';
import './App.css';

export default function App() {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [view, setView] = useState('student');
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
  const [confirmEmailInput, setConfirmEmailInput] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const handlingLinkRef = useRef(false);

  // If the sign-in link is opened somewhere other than where it was
  // requested (e.g. requested from the home-screen app, opened in Safari),
  // cc_pending_email won't be found — iOS standalone web apps don't
  // reliably share localStorage with Safari. window.prompt() used to be the
  // fallback here, but it's known to silently no-op (never render, just
  // return null) in standalone home-screen web apps, and can be suppressed
  // in Safari too when not fired from a direct tap — so pause and ask via
  // real UI (below) instead, which is guaranteed to render.
  const completeLinkIfPresent = useCallback(async (emailOverride) => {
    const auth = getFirebaseAuth();
    if (handlingLinkRef.current) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    handlingLinkRef.current = true;
    try {
      const email = emailOverride || window.localStorage.getItem('cc_pending_email');
      if (!email) {
        setNeedsEmailConfirm(true);
        return;
      }
      try {
        await signInWithEmailLink(auth, email, window.location.href);
        window.localStorage.removeItem('cc_pending_email');
        setNeedsEmailConfirm(false);
        // Strip the link's query string so a refresh doesn't try to replay it
        window.history.replaceState({}, '', window.location.pathname);
      } catch (err) {
        toast.error('Sign-in link is invalid or expired. Request a new one.', { duration: 6000 });
        // If this was a typed-in retry from the confirm form, keep the
        // oobCode in the URL so a corrected email can be retried — stripping
        // it here would leave the confirm form as a dead end.
        if (!emailOverride) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    } finally {
      handlingLinkRef.current = false;
    }
  }, []);

  async function handleConfirmEmailSubmit(e) {
    e.preventDefault();
    setConfirmLoading(true);
    try {
      await completeLinkIfPresent(confirmEmailInput.trim());
    } finally {
      setConfirmLoading(false);
    }
  }

  function startOver() {
    window.localStorage.removeItem('cc_pending_email');
    window.history.replaceState({}, '', window.location.pathname);
    setNeedsEmailConfirm(false);
    setConfirmEmailInput('');
  }

  useEffect(() => {
    const auth = getFirebaseAuth();
    let unsubscribe = () => {};

    completeLinkIfPresent().then(() => {
      unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
        if (!fbUser) { setUser(null); setReady(true); return; }
        try {
          setUser(await api.getMe());
        } catch (err) {
          toast.error(err.message || 'No ClassCheck account found for this email.');
          await signOut(auth);
          setUser(null);
        }
        setReady(true);
      });
    });

    // iOS can route a tapped mail link into an already-running installed
    // home-screen app instead of doing a fresh load — the URL changes but
    // this effect (mount-only) never re-fires, so the pending link is
    // silently missed. Re-check whenever the app regains visibility.
    function onVisible() {
      if (document.visibilityState === 'visible') completeLinkIfPresent();
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, [completeLinkIfPresent]);

  // Initialize Firebase messaging and request notification permission once logged in
  useEffect(() => {
    if (!user) return;
    // Run Firebase init in background - don't block app rendering
    initFirebase()
      .then(() => {
        setupForegroundNotifications();
        requestNotificationPermission(user.id);
      })
      .catch(err => {
        // Firebase failed - app still works, just no push notifications
        console.log('Firebase init failed:', err.message);
      });
  }, [user]);

  // <Toaster> used to live only inside the post-login JSX below, so every
  // toast.error() called before login (link-completion failures, Setup's
  // own errors) had nowhere to render — the app just silently sat there.
  // Mount it once here so it's always present regardless of which screen
  // below is showing.
  let content;
  if (needsEmailConfirm) {
    content = (
      <div className="setup-screen">
        <div className="setup-card">
          <h1>📚 ClassCheck</h1>
          <p>Confirm your email to finish signing in.</p>
          <form onSubmit={handleConfirmEmailSubmit}>
            <label>
              Email
              <input
                type="email"
                placeholder="you@example.com"
                value={confirmEmailInput}
                onChange={e => setConfirmEmailInput(e.target.value)}
                required
                autoFocus
              />
            </label>
            <button type="submit" disabled={confirmLoading || !confirmEmailInput.trim()}>
              {confirmLoading ? 'Signing in…' : 'Confirm'}
            </button>
          </form>
          <button type="button" className="btn-link" onClick={startOver}>
            Start over / request a new link
          </button>
        </div>
      </div>
    );
  } else if (!ready) {
    content = <div className="loading">Loading…</div>;
  } else if (!user) {
    content = <Setup />;
  } else {
    content = (
      <div className="app">
        <header className="app-header">
          <h1>📚 ClassCheck</h1>
          <div className="header-right">
            <span className="user-name">{user.name}</span>
            {user.role === 'admin' && (
              <div className="view-toggle">
                <button className={view === 'student' ? 'active' : ''} onClick={() => setView('student')}>My Classes</button>
                <button className={view === 'admin' ? 'active' : ''} onClick={() => setView('admin')}>Admin</button>
                <button className={view === 'videos' ? 'active' : ''} onClick={() => setView('videos')}>Videos</button>
              </div>
            )}
            <button className="btn-link" onClick={() => { signOut(getFirebaseAuth()); setUser(null); }}>Sign out</button>
          </div>
        </header>
        <main>
          {view === 'admin' && user.role === 'admin'
            ? <AdminView currentUser={user} />
            : view === 'videos' && user.role === 'admin'
              ? <VideoAdmin />
              : <StudentView currentUser={user} />
          }
        </main>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" />
      {content}
    </>
  );
}
